import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { DtcEntry } from '../../utils/dtc';

/**
 * One decoded DTC. Descriptions reach ~180 characters, which is roughly eight
 * wrapped lines in the 320px panel, so the text is clamped to three lines by
 * default with the full string on `title` (mouse hover) AND behind an
 * in-place disclosure control (touch/keyboard) — clamping alone left touch
 * and keyboard users with no way to read past line three. Design doc §8.
 *
 * The description itself is the control: a real `<button>` (not a hand-rolled
 * click handler on a `<p>`) so it's focusable and activates on Enter/Space
 * for free, with `aria-expanded` announcing state. Each row owns its own
 * `expanded` state, so expanding one code never affects its siblings.
 *
 * The button's accessible name is a concise `aria-label` ("Expand/Collapse
 * description for SPN … / FMI …"), NOT the description text it wraps.
 * Without it, the accessible name is computed from the button's content —
 * the full ~180-char description — so a screen reader would announce that
 * whole string on focus, before role or state. `title` still carries the
 * full text for mouse hover; the visible description remains normal content.
 *
 * Collapsed, clamped text ending in "…" is visually indistinguishable from
 * plain truncated text — nothing signals it's a control. A chevron makes the
 * disclosure discoverable at a glance (rotates 180° when expanded) for the
 * touch users this control exists for, who have no hover affordance to find
 * it otherwise. It's `aria-hidden` because the button's accessible name
 * already comes from `aria-label` above — the icon carries no information a
 * screen reader user needs and must not be announced as separate content.
 *
 * Only `grounding: 'generic'` changes rendering — 'oem', 'sae' and 'grounded'
 * all mean "trust this text". Design doc §7.4.
 */
export function DtcRow({ entry }: { entry: DtcEntry }) {
  const [expanded, setExpanded] = useState(false);
  const ungrounded = entry.grounding === 'generic';

  return (
    <div className="rounded-lg bg-ems-bg px-2.5 py-2 border-l-2 border-l-ems-blue">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-xs font-semibold text-ems-text-primary">
          SPN {entry.spn} / FMI {entry.fmi}
        </span>
        <span className="font-mono text-[10px] text-ems-text-muted shrink-0">
          &times;{entry.oc}
        </span>
      </div>
      {entry.description && (
        <button
          type="button"
          title={entry.description}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} description for SPN ${entry.spn} / FMI ${entry.fmi}`}
          onClick={() => setExpanded((prev) => !prev)}
          className={cn(
            'mt-1 flex w-full items-start gap-1 border-0 bg-transparent p-0 text-left font-sans',
            'focus:outline-none focus:ring-2 focus:ring-ems-blue/40 rounded',
          )}
        >
          <ChevronDown
            aria-hidden="true"
            data-testid="dtc-chevron"
            className={cn(
              'mt-0.5 h-3 w-3 shrink-0 text-ems-text-muted transition-transform',
              expanded && 'rotate-180',
            )}
          />
          <span
            className={cn(
              'text-[11px] leading-snug text-ems-text-secondary',
              !expanded && 'line-clamp-3',
            )}
          >
            {entry.description}
          </span>
        </button>
      )}
      {ungrounded && (
        <span
          data-testid="dtc-ungrounded"
          className="mt-1 block text-[10px] italic text-ems-text-muted"
        >
          Consult engine ECM manual
        </span>
      )}
    </div>
  );
}
